import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { QRCodeCanvas } from "qrcode.react";
import EventQuestionsEditor from "../components/EventQuestionsEditor";

type EventRow = {
  id: string;
  campaign_id: string;
  title: string;
  description: string | null;
  location: string | null;
  start_at: string;
};

type Signup = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
  answers: any;
};

export default function EventDetail() {
  const { id } = useParams();
  const [ev, setEv] = useState<EventRow | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [signups, setSignups] = useState<Signup[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Branding/welcome (stored on event_forms)
  const [welcome, setWelcome] = useState<string>("");
  const [brandUrl, setBrandUrl] = useState<string>("");

  const checkinUrl = useMemo(
    () => (token ? `${window.location.origin}/e/${token}` : ""),
    [token]
  );

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data: e } = await supabase
        .from("events")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (e) setEv(e as EventRow);

      const { data: f } = await supabase
        .from("event_forms")
        .select("token,is_open,welcome_message,brand_image_url")
        .eq("event_id", id)
        .maybeSingle();
      if (f) {
        setToken(f.token);
        setIsOpen(!!f.is_open);
        setWelcome(f.welcome_message ?? "");
        setBrandUrl(f.brand_image_url ?? "");
      }

      const { data: s } = await supabase
        .from("event_signups")
        .select("id,name,email,phone,answers,created_at")
        .eq("event_id", id)
        .order("created_at", { ascending: false });
      setSignups((s ?? []) as Signup[]);
    })();
  }, [id]);

  async function enableCheckin() {
    if (!id) return;
    const { data, error } = await supabase.rpc("create_event_form", {
      p_event_id: id,
    });
    if (error) {
      setErr(error.message);
      return;
    }
    setToken(data as string);
    setIsOpen(true);
    setMsg("Check-in enabled.");
  }

  async function toggleOpen(v: boolean) {
    if (!id) return;
    const { error } = await supabase
      .from("event_forms")
      .update({ is_open: v })
      .eq("event_id", id);
    if (error) {
      setErr(error.message);
      return;
    }
    setIsOpen(v);
    setMsg(v ? "Form opened." : "Form closed.");
  }

  function copyLink() {
    if (checkinUrl) {
      navigator.clipboard.writeText(checkinUrl);
      setMsg("Link copied.");
    }
  }

  // ---------- Branding helpers ----------
  async function saveBranding() {
    if (!id) return;
    const { error } = await supabase
      .from("event_forms")
      .update({
        welcome_message: welcome || null,
        brand_image_url: brandUrl || null,
      })
      .eq("event_id", id);
    if (error) {
      setErr(error.message);
      return;
    }
    setMsg("Branding saved.");
  }

  async function uploadBrandImage(file: File) {
    try {
      setErr(null);
      setMsg(null);
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id;
      if (!uid) throw new Error("Not signed in");
      const path = `${uid}/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
      const { error: upErr } = await supabase.storage
        .from("branding")
        .upload(path, file);
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage
        .from("branding")
        .getPublicUrl(path);
      setBrandUrl(pub.publicUrl);
      setMsg("Image uploaded. Click “Save Branding” to apply.");
    } catch (e: any) {
      setErr(e.message ?? "Upload failed");
    }
  }

  // Let the user choose from existing uploads (not just the latest)
  async function chooseExisting() {
    try {
      setErr(null);
      setMsg(null);
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id;
      if (!uid) throw new Error("Not signed in");

      const { data: files, error } = await supabase.storage
        .from("branding")
        .list(uid, { limit: 200 });
      if (error) throw error;
      if (!files?.length) {
        setMsg("No images yet. Upload one.");
        return;
      }

      // Simple prompt selector
      const sorted = [...files].sort((a, b) =>
        (b.created_at || "").localeCompare(a.created_at || "")
      );
      const menu = sorted.map((f, i) => `${i + 1}) ${f.name}`).join("\n");
      const choice = window.prompt(
        `Select an image by number:\n\n${menu}\n\n(Leave blank to cancel)`
      );
      if (!choice) return;
      const idx = Number(choice) - 1;
      if (Number.isNaN(idx) || idx < 0 || idx >= sorted.length) {
        setErr("Invalid selection.");
        return;
      }
      const pick = sorted[idx];
      const { data: pub } = supabase.storage
        .from("branding")
        .getPublicUrl(`${uid}/${pick.name}`);
      setBrandUrl(pub.publicUrl);
      setMsg("Selected image. Click “Save Branding” to apply.");
    } catch (e: any) {
      setErr(e.message ?? "Could not list images");
    }
  }

  // Extract storage path from a public URL: returns "<uid>/<filename>" or null
  function storagePathFromPublicUrl(url: string): string | null {
    try {
      const marker = "/branding/";
      const idx = url.indexOf(marker);
      if (idx === -1) return null;
      return url.slice(idx + marker.length);
    } catch {
      return null;
    }
  }

  // Remove the branding image (keeps the file in storage)
  async function removeBrandImage() {
    if (!id) return;
    const ok = window.confirm("Remove the branding image from this form?");
    if (!ok) return;
    const { error } = await supabase
      .from("event_forms")
      .update({ brand_image_url: null })
      .eq("event_id", id);
    if (error) {
      setErr(error.message);
      return;
    }
    setBrandUrl("");
    setMsg("Branding image removed. (File not deleted from storage.)");
  }

  // Remove from form AND delete the file from your storage (if in your UID folder)
  async function removeBrandImageAndDelete() {
    if (!id || !brandUrl) return;
    const ok = window.confirm(
      "Remove from form and DELETE the image file from storage?"
    );
    if (!ok) return;

    // First, clear it from the form
    const { error: e1 } = await supabase
      .from("event_forms")
      .update({ brand_image_url: null })
      .eq("event_id", id);
    if (e1) {
      setErr(e1.message);
      return;
    }

    // Then try to delete the underlying file (only if it’s under your UID folder)
    let deleted = false;
    try {
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id;
      const path = storagePathFromPublicUrl(brandUrl);
      if (uid && path && path.startsWith(uid + "/")) {
        const { error: delErr } = await supabase.storage
          .from("branding")
          .remove([path]);
        if (delErr) throw delErr;
        deleted = true;
      }
    } catch (e: any) {
      setErr(e.message ?? "File delete failed (branding link was removed).");
    }

    setBrandUrl("");
    setMsg(
      deleted
        ? "Branding image removed and file deleted."
        : "Branding image removed."
    );
  }
  // ---------- /Branding helpers ----------

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ marginTop: 0 }}>{ev?.title ?? "Event"}</h1>

      {(msg || err) && (
        <div
          style={{
            marginBottom: 10,
            padding: "10px 12px",
            borderRadius: 10,
            background: msg ? "#ECFDF5" : "#FEF2F2",
            border: `1px solid ${msg ? "#A7F3D0" : "#FCA5A5"}`,
            color: msg ? "#065F46" : "#991B1B",
          }}
        >
          {msg || err}
        </div>
      )}

      {/* Event meta */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ color: "#6b7280" }}>
          {ev?.start_at ? new Date(ev.start_at).toLocaleString() : ""}
          {ev?.location ? " • " + ev.location : ""}
        </div>
        {ev?.description && <div style={{ marginTop: 8 }}>{ev.description}</div>}
      </div>

      {/* Check-in / QR */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          {!token ? (
            <button onClick={enableCheckin} style={primaryBtn}>
              Enable Check-in
            </button>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  justifyItems: "center",
                  gap: 8,
                }}
              >
                <QRCodeCanvas value={checkinUrl} size={220} />
                <div
                  style={{
                    wordBreak: "break-all",
                    fontSize: 12,
                    color: "#6b7280",
                  }}
                >
                  {checkinUrl}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={copyLink} style={secondaryBtn}>
                    Copy link
                  </button>
                  <button
                    onClick={() => toggleOpen(!isOpen)}
                    style={secondaryBtn}
                  >
                    {isOpen ? "Close form" : "Open form"}
                  </button>
                  {id && token && (
                    <Link to={`/events/${id}/qr-print`} style={secondaryBtn}>
                      Open printable poster
                    </Link>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Branding & Welcome */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 8 }}>
          Public Form Branding
        </div>

        <label style={{ fontSize: 13, color: "#374151" }}>
          Welcome / Thank-you message
        </label>
        <textarea
          value={welcome}
          onChange={(e) => setWelcome(e.target.value)}
          rows={3}
          placeholder="Thanks for attending! Please check in below…"
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            margin: "6px 0 12px",
          }}
        />

        <div>
          <label style={{ fontSize: 13, color: "#374151" }}>
            Brand image (logo/flyer)
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              e.target.files && uploadBrandImage(e.target.files[0])
            }
          />

          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button onClick={chooseExisting} style={secondaryBtn}>
              Choose existing
            </button>
            <button onClick={saveBranding} style={primaryBtn}>
              Save Branding
            </button>
            {brandUrl && (
              <>
                <button onClick={removeBrandImage} style={dangerBtn}>
                  Remove image
                </button>
                <button onClick={removeBrandImageAndDelete} style={dangerBtn}>
                  Remove + delete file
                </button>
              </>
            )}
          </div>

          {brandUrl && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 13, color: "#6b7280" }}>Preview</div>
              <img src={brandUrl} alt="Brand" style={{ maxWidth: 220, maxHeight: 120 }} />
            </div>
          )}
        </div>
      </div>

      {/* Custom Questions editor */}
      {id && <EventQuestionsEditor eventId={id} />}

      {/* Attendees */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontWeight: 700 }}>Signups ({signups.length})</div>
          <button
            onClick={() => exportCsv(signups, ev?.title ?? "event")}
            style={secondaryBtn}
          >
            Export CSV
          </button>
        </div>

        <div>
          {signups.map((s) => (
            <div
              key={s.id}
              style={{ borderTop: "1px solid #f1f5f9", padding: "10px 0" }}
            >
              <div style={{ fontWeight: 700 }}>{s.name || "—"}</div>
              <div style={{ color: "#6b7280", fontSize: 13 }}>
                {s.email || "—"} {s.phone ? " • " + s.phone : ""}
              </div>
              {!!s.answers && Object.keys(s.answers).length > 0 && (
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  {Object.entries(s.answers).map(([k, v]) => (
                    <div key={k}>
                      <strong>{k}:</strong> {formatAns(v)}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ color: "#9ca3af", fontSize: 12 }}>
                {new Date(s.created_at).toLocaleString()}
              </div>
            </div>
          ))}
          {signups.length === 0 && (
            <div style={{ color: "#6b7280" }}>No signups yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function exportCsv(rows: Signup[], title: string) {
  const baseCols = ["name", "email", "phone", "created_at"];
  const customKeys = Array.from(
    new Set(rows.flatMap((r) => Object.keys(r.answers || {})))
  );
  const cols = [...baseCols, ...customKeys];
  const lines = [cols.join(",")];
  for (const r of rows) {
    const line = cols
      .map((c) => {
        const raw = c in (r as any) ? (r as any)[c] : (r.answers || {})[c];
        const val =
          raw == null
            ? ""
            : typeof raw === "object"
            ? JSON.stringify(raw)
            : String(raw);
        return `"${val.replaceAll('"', '""')}"`;
      })
      .join(",");
    lines.push(line);
  }
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/\s+/g, "_")}_signups.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function formatAns(v: any) {
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object" && v !== null) return JSON.stringify(v);
  return String(v);
}

const primaryBtn: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "none",
  background: "#0B2E5B",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};
const secondaryBtn: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #0B2E5B",
  background: "#fff",
  color: "#0B2E5B",
  fontWeight: 700,
  cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  ...secondaryBtn,
  borderColor: "#ef4444",
  color: "#ef4444",
};
